import { IslandApiResponse } from 'state/data/slice'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'

import {
  AprCalculationParams,
  AprDisplayConfig,
  AprDisplayData,
  AprDisplayParams,
  DepositOption,
  ExternalLink,
  ExternalLinksParams,
  PoolConfig,
  PoolData,
  PoolMetrics,
  StakingData,
  StakingDataParams,
} from '../types'

export class CharmPoolConfig implements PoolConfig {
  readonly type = 'Charm' as const
  readonly provider = 'charm' as const
  readonly supportsZapping = true // Via Enzo
  readonly supportsAutoCompound = false // Native charm auto-compound
  readonly supportsFarms = true
  readonly supportsVaults = true

  getPoolData(poolApiResponse: IslandApiResponse): PoolData {
    return {
      lowerTick: poolApiResponse?.lowerTick ?? 0,
      upperTick: poolApiResponse?.upperTick ?? 0,
      currentTick: poolApiResponse?.currentTick ?? 0,
      feeTier: poolApiResponse?.feeTier,
      poolAddress: poolApiResponse?.id,
      lpTokenAddress: poolApiResponse?.id,
    }
  }

  getStakingData(params: StakingDataParams): StakingData {
    const {
      poolApiResponse,
      kodiakFarmAddress,
      rewardVaultAddress,
      migrationKodiakFarmAddress,
      isWhitelistedForRewardVault,
    } = params

    const isValidKodiakFarm = !!kodiakFarmAddress

    let stakingType: StakingData['stakingType'] = 'none'

    if (isWhitelistedForRewardVault && rewardVaultAddress) {
      stakingType = 'rewardVault'
    } else if (kodiakFarmAddress && !rewardVaultAddress) {
      stakingType = 'kodiakFarm'
    }

    const baultAddress = this.getBaultAddress(params.poolApiResponse, stakingType)

    return {
      kodiakFarmAddress: isValidKodiakFarm ? kodiakFarmAddress : undefined,
      rewardVaultAddress: isWhitelistedForRewardVault ? rewardVaultAddress : undefined,
      migrationKodiakFarmAddress,
      baultAddress,
      stakingType,
      baultFarmAddress: '',
    }
  }

  getPoolMetrics(poolApiResponse: any): PoolMetrics {
    return {
      tvl: poolApiResponse?.tvl ?? 0,
      farmTvl: poolApiResponse?.farmTvl,
      baseApr: poolApiResponse?.apr,
      farmApr: !poolApiResponse?.isRewardVault ? poolApiResponse?.farm?.apr : undefined,
      rewardVaultRewardApr: poolApiResponse?.isRewardVault ? poolApiResponse?.farm?.apr : undefined,
      weeklyFees: poolApiResponse?.weeklyFeesEarnedUSD ?? 0,
    }
  }

  getExternalLinks({
    chainId,
    poolApiResponse,
    kodiakFarmAddress,
    rewardVaultAddress,
    baultAddress,
  }: ExternalLinksParams): ExternalLink[] {
    const links: ExternalLink[] = []

    if (poolApiResponse?.id) {
      links.push({
        label: 'Charm Vault Contract',
        url: getExplorerLink(chainId, poolApiResponse.id, ExplorerDataType.ADDRESS),
        copyText: poolApiResponse.id,
      })
    }

    if (poolApiResponse?.pool?.id) {
      links.push({
        label: 'Pool Contract',
        url: getExplorerLink(chainId, poolApiResponse.pool.id, ExplorerDataType.ADDRESS),
        copyText: poolApiResponse.pool.id,
      })
    }

    if (kodiakFarmAddress) {
      links.push({
        label: 'Farm Contract',
        url: getExplorerLink(chainId, kodiakFarmAddress, ExplorerDataType.ADDRESS),
        copyText: kodiakFarmAddress,
      })
    }

    if (rewardVaultAddress) {
      links.push(
        {
          label: 'Reward Vault Contract',
          url: getExplorerLink(chainId, rewardVaultAddress, ExplorerDataType.ADDRESS),
          copyText: rewardVaultAddress,
        },
        {
          label: 'Berachain Hub',
          url: `https://hub.berachain.com/vaults/${rewardVaultAddress}`,
        }
      )
    }

    if (baultAddress) {
      links.push({
        label: 'Bault Contract',
        url: getExplorerLink(chainId, baultAddress, ExplorerDataType.ADDRESS),
        copyText: baultAddress,
      })
    }

    return links
  }

  getAprDisplay({
    isRewardVaultActive,
    isKodiakFarmActive,
    rewardVaultRewardSymbol,
  }: AprDisplayParams): AprDisplayConfig {
    return {
      showBaseApr: true,
      showKodiakFarmApr: isKodiakFarmActive && !isRewardVaultActive,
      showRewardVaultRewardApr: isRewardVaultActive,
      farmLabel: isRewardVaultActive ? `${rewardVaultRewardSymbol || 'Reward'} APR` : 'Farm APR',
    }
  }

  getDepositOptions(): DepositOption[] {
    return [
      { value: 'depositOnly', label: 'Deposit with Auto-Compound', disabled: false },
      { value: 'withStaking', label: 'With Staking', disabled: false },
    ]
  }

  getDisplayApr({ farmApr, isLoadingFarm, metrics, aprDisplay }: AprCalculationParams): AprDisplayData | null {
    if (!aprDisplay.showKodiakFarmApr && !aprDisplay.showRewardVaultRewardApr) {
      return null
    }

    if (aprDisplay.showKodiakFarmApr && farmApr !== null) {
      return {
        apr: farmApr,
        label: aprDisplay.farmLabel,
        isLoading: isLoadingFarm,
      }
    }

    if (aprDisplay.showRewardVaultRewardApr && metrics.rewardVaultRewardApr !== undefined) {
      return {
        apr: metrics.rewardVaultRewardApr,
        label: aprDisplay.farmLabel,
        isLoading: false,
      }
    }

    return null
  }

  private getBaultAddress(poolApiResponse: any, stakingType: StakingData['stakingType']): string | undefined {
    if (stakingType !== 'rewardVault') return undefined
    if (!poolApiResponse?.isRewardVault) return undefined
    return poolApiResponse?.baults?.[poolApiResponse.baults.length - 1]?.id
  }
}
